/**
 * ComparisonPowerBars - Visual bar chart comparison for multiple products
 *
 * Shows horizontal bars for price and weight where bar width = value relative to max.
 * Color coding: green = best, amber = mid, red = worst.
 * Only shows when 2+ products are being compared.
 */

interface ComparisonItem {
  product_id: string;
  product_name: string;
  price_gbp: number;
  weight_g: number | null;
}

interface ComparisonPowerBarsProps {
  /** Products being compared */
  items: ComparisonItem[];
}

/** Get bar color based on rank (1 = best) */
function getBarColor(rank: number, total: number): string {
  if (total <= 1) return 'bg-neutral-500';
  if (rank === 1) return 'bg-green-500';
  if (rank === total) return 'bg-red-500';
  return 'bg-amber-500';
}

/** Get label for rank */
function getRankLabel(rank: number, total: number): string {
  if (total <= 1) return '';
  if (rank === 1) return 'Best';
  if (rank === total) return 'Highest';
  return '';
}

interface BarRowProps {
  label: string;
  items: Array<{
    id: string;
    name: string;
    value: number;
    formattedValue: string;
    rank: number;
  }>;
  /** Whether lower is better (true for price/weight) */
  lowerIsBetter?: boolean;
}

function BarRow({ label, items, lowerIsBetter = true }: BarRowProps) {
  const maxValue = Math.max(...items.map((i) => i.value));
  const total = items.length;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-neutral-400 uppercase tracking-wider">
        {label}
      </h4>
      <div className="space-y-3">
        {items.map((item) => {
          // For lower-is-better, rank 1 means lowest value
          const effectiveRank = lowerIsBetter
            ? item.rank
            : total - item.rank + 1;
          const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          const barColor = getBarColor(effectiveRank, total);
          const rankLabel = getRankLabel(effectiveRank, total);

          return (
            <div key={item.id} className="flex items-center gap-3">
              <span className="w-32 text-sm text-neutral-300 truncate" title={item.name}>
                {item.name}
              </span>
              <div className="flex-1 h-4 bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className={`h-full ${barColor} rounded-full transition-all duration-300`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="font-mono text-sm text-white w-20 text-right">
                {item.formattedValue}
              </span>
              {rankLabel && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    effectiveRank === 1
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {rankLabel}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ComparisonPowerBars({ items }: ComparisonPowerBarsProps) {
  // Only show with 2+ items
  if (items.length < 2) {
    return null;
  }

  // Calculate price rankings (lower is better)
  const priceItems = [...items]
    .sort((a, b) => a.price_gbp - b.price_gbp)
    .map((item, index) => ({
      id: item.product_id,
      name: item.product_name,
      value: item.price_gbp,
      formattedValue: `£${item.price_gbp}`,
      rank: index + 1,
    }));

  // Calculate weight rankings (lower is better), filter out nulls
  const validWeightItems = items.filter(
    (item): item is ComparisonItem & { weight_g: number } =>
      item.weight_g !== null
  );

  const weightItems = [...validWeightItems]
    .sort((a, b) => a.weight_g - b.weight_g)
    .map((item, index) => ({
      id: item.product_id,
      name: item.product_name,
      value: item.weight_g,
      formattedValue:
        item.weight_g >= 1000
          ? `${(item.weight_g / 1000).toFixed(1)}kg`
          : `${item.weight_g}g`,
      rank: index + 1,
    }));

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-6">
      <h3 className="text-lg font-semibold text-white">Quick Comparison</h3>

      {/* Price comparison */}
      <BarRow label="Price" items={priceItems} lowerIsBetter />

      {/* Weight comparison (only if we have weight data) */}
      {weightItems.length >= 2 && (
        <BarRow label="Weight" items={weightItems} lowerIsBetter />
      )}

      {/* Summary stats */}
      <div className="pt-4 border-t border-neutral-800 flex flex-wrap gap-6 text-sm">
        <div>
          <span className="text-neutral-400">Price Range: </span>
          <span className="text-white font-mono">
            £{Math.min(...items.map((i) => i.price_gbp))} - £
            {Math.max(...items.map((i) => i.price_gbp))}
          </span>
          <span className="text-neutral-500 ml-2">
            (£
            {Math.max(...items.map((i) => i.price_gbp)) -
              Math.min(...items.map((i) => i.price_gbp))}{' '}
            diff)
          </span>
        </div>
        {validWeightItems.length >= 2 && (
          <div>
            <span className="text-neutral-400">Weight Range: </span>
            <span className="text-white font-mono">
              {Math.min(...validWeightItems.map((i) => i.weight_g))}g -{' '}
              {Math.max(...validWeightItems.map((i) => i.weight_g))}g
            </span>
            <span className="text-neutral-500 ml-2">
              ({Math.max(...validWeightItems.map((i) => i.weight_g)) -
                Math.min(...validWeightItems.map((i) => i.weight_g))}
              g diff)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
